import AddCircle from "@mui/icons-material/AddCircle";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CommentIcon from "@mui/icons-material/Comment";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";
import { Checkbox, Chip, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { DateTime } from "luxon";
import React from "react";
import { Link } from "react-router-dom";
import { copyJsonObject } from "../commonUtils";
import { EMPLOYEE_TEMP_LABEL, FEATURES, SENIORITY_MISSING_DATA } from "../constants";
import { parseCSVRow } from "../csvUtils";
import { getThanksGivingDateOfTheYear } from "../dateHelper";
import { convertIsoDateToLuxon, convertMonthStringToInteger, set_time_to_midnight } from "../dateTimeFormatter";
import { sortSeniorityList } from "../filterSeniorityList";
import { BID_GROUP_ADD_EMPLOYEE_WARNING_MESSAGE } from "../messageConstants";
import { getTimeZoneFromSwaStation } from "../timeZoneService";
import { isAwardedBid, isDeletedBid, isPendingBid } from "./bidCommon.js";
import { BID_TYPE, NC_BID_AWARD_TYPE } from "./bidConstants";
import { getEligibleEmployees, getEmployeeUnrankedCount } from "./bidGroupUtils";
import {
  NC_PTO_FTO_BALANCES_IMPORT_ERROR_MESSAGE,
  NC_PTO_GOC_HOL_BALANCES_IMPORT_ERROR_MESSAGE,
} from "./messageConstants";
import { getFirstDayWithMonthOffset, getNonEmptyChoices, isHoliday } from "./timeOffBidUtils";

export const getDefaultCalendarMonth = (station, schedMonth, schedYear) => {
  const minDate = DateTime.max(
    set_time_to_midnight(DateTime.now().setZone(getTimeZoneFromSwaStation(station))),
    getFirstDayWithMonthOffset(
      schedMonth,
      schedYear,
      process.env.REACT_APP_NON_CONTRACT_ALLOWED_BID_WINDOW_MONTHS_RANGE_PRIOR_TO_BID_EFFECTIVE_MONTH
    )
  );

  return minDate.toJSDate();
};

export const getBidGroupColumns = (
  { bidReferenceNumber, bidGroup, bidType, bidStatus, rankStyle, initialBidGroup, userSessionEid, bidStation },
  handleIneligibleClick,
  handleRemoveEmployee,
  handleAddRemovedEmployee,
  handleEmployeeChoicesAsAdmin,
  setSelectedEmployeeForAudit
) => {
  let columns = [];
  let baseColumns = [
    {
      field: "ineligible",
      headerName: "Ineligible",
      width: 80,
      type: "boolean",
      sortable: false,
      renderCell: (params) => {
        return (
          <Checkbox
            checked={!params.row.isEligible}
            onChange={() => {
              handleIneligibleClick(params.row.eid);
            }}
            disabled={
              !!params.row.isRemoved || params.row.rank === "-" || isDeletedBid(bidStatus) || isAwardedBid(bidStatus)
            }
          />
        );
      },
    },
    {
      field: "rank",
      headerName: "Rank",
      minWidth: 70,
      type: "string",
      sortingOrder: ["asc", "desc"],
      renderCell: (params) => {
        return (
          <TextField
            variant="standard"
            InputProps={{
              className: rankStyle,
            }}
            size="small"
            value={params.row.rank ? params.row.rank : ""}
            required
            error={params.row.rank === ""}
            disabled={!!params.row.isRemoved || isAwardedBid(bidStatus)}
          />
        );
      },
      flex: 0.12,
      sortComparator: sortSeniorityList,
      editable: !(isAwardedBid(bidStatus) || isDeletedBid(bidStatus)),
    },
    {
      field: "eid",
      headerName: "EID",
      type: "string",
      sortable: false,
      minWidth: 100,
      flex: 0.15,
    },
    {
      field: "name",
      headerName: "Name",
      type: "string",
      minWidth: 220,
      sortable: false,
      renderCell: (params) => {
        return (
          <>
            {isEmployeeEligibleForNCBidChoices(params.row, initialBidGroup, bidStatus, userSessionEid) ? (
              <Typography
                style={{ cursor: "pointer" }}
                fontSize="14px"
                onClick={() => {
                  handleEmployeeChoicesAsAdmin(params.row);
                }}
              >
                <u>
                  {params.row.lastName}, {params.row.shortName}
                </u>
              </Typography>
            ) : (
              <>
                {params.row.lastName}, {params.row.shortName}
              </>
            )}
            {params.row.isTemp && params.row.homeStation !== bidStation && (
              <Chip sx={{ marginLeft: "5px" }} label={EMPLOYEE_TEMP_LABEL} />
            )}
          </>
        );
      },
      flex: 0.3,
    },
    {
      field: "ptoBalance",
      headerName: "PTO Balance",
      type: "string",
      sortable: false,
      minWidth: 120,
      renderCell: (params) => {
        return (
          <TextField
            variant="standard"
            InputProps={{
              className: rankStyle,
            }}
            size="small"
            value={params.row.ptoBalance}
            required
            error={params.row.ptoBalance === ""}
            disabled={!!params.row.isRemoved || isAwardedBid(bidStatus) || isDeletedBid(bidStatus)}
          />
        );
      },
      flex: 0.15,
      editable: !(isAwardedBid(bidStatus) || isDeletedBid(bidStatus)),
    },
  ];

  if (bidType === BID_TYPE.NC_PTO_FTO) {
    baseColumns.push({
      field: "ftoBalance",
      headerName: "FTO Balance",
      type: "string",
      sortable: false,
      minWidth: 120,
      renderCell: (params) => {
        return (
          <TextField
            variant="standard"
            InputProps={{
              className: rankStyle,
            }}
            size="small"
            value={params.row.ftoBalance}
            required
            error={params.row.ftoBalance === ""}
            disabled={!!params.row.isRemoved || isAwardedBid(bidStatus) || isDeletedBid(bidStatus)}
          />
        );
      },
      flex: 0.15,
      editable: !(isAwardedBid(bidStatus) || isDeletedBid(bidStatus)),
    });
  }

  if (bidType === BID_TYPE.NC_PTO_GOC_HOL) {
    baseColumns.push({
      field: "gocHolBalance",
      headerName: "GOC-HOL Balance",
      type: "string",
      sortable: false,
      minWidth: 150,
      renderCell: (params) => {
        return (
          <TextField
            variant="standard"
            InputProps={{
              className: rankStyle,
            }}
            size="small"
            value={params.row.gocHolBalance}
            required
            error={params.row.gocHolBalance === ""}
            disabled={!!params.row.isRemoved || isAwardedBid(bidStatus) || isDeletedBid(bidStatus)}
          />
        );
      },
      flex: 0.15,
      editable: !(isAwardedBid(bidStatus) || isDeletedBid(bidStatus)),
    });
  }

  baseColumns.push({
    field: "phoneNumber",
    headerName: "Phone #",
    type: "string",
    minWidth: 120,
    sortable: false,
    renderCell: (params) => {
      if (!params.row.phoneNumber) return "-";
      else return params.row.phoneNumber;
    },
    flex: 0.15,
  });

  // Non-contract seniority list columns
  let contractBasedColumns = [];
  contractBasedColumns.push(
    ...[
      {
        field: "seniorityDate",
        headerName: "Seniority Date",
        type: "dateTime",
        minWidth: 170,
        sortable: false,
        renderCell: (params) => {
          if (params.row.seniorityDate === "" || params.row.seniorityDate === SENIORITY_MISSING_DATA) return "-";
          return params.row.seniorityDate;
        },
        flex: 0.25,
      },
      {
        field: "hireDate",
        headerName: "Hire Date",
        type: "dateTime",
        minWidth: 120,
        sortable: false,
        renderCell: (params) => {
          if (params.row.hireDate === "" || params.row.hireDate === SENIORITY_MISSING_DATA) return "-";
          return params.row.hireDate;
        },
        flex: 0.15,
      },
      {
        field: "birthDate",
        headerName: "Birth Date",
        type: "dateTime",
        minWidth: 120,
        sortable: false,
        renderCell: (params) => {
          if (params.row.birthDate === "" || params.row.birthDate === SENIORITY_MISSING_DATA) return "-";
          return params.row.birthDate;
        },
        flex: 0.15,
      },
      {
        field: "baseSchedule",
        headerName: "Base Schedule",
        type: "string",
        minWidth: 120,
        sortable: false,
        renderCell: (params) => {
          if (isEmployeeEligibleForBaseSchedule(params.row, initialBidGroup, bidStatus, bidStation)) {
            return (
              <Link
                to={{
                  pathname: `/${FEATURES.SCHEDULE_MANAGEMENT}/${bidReferenceNumber}/${params.row.eid}`,
                }}
                target="_blank"
              >
                <IconButton>
                  <CalendarMonthIcon />
                </IconButton>
              </Link>
            );
          }
        },
        flex: 0.15,
      },
    ]
  );

  const staticRightColumns = [
    {
      headerName: "",
      field: "comments",
      type: "string",
      width: 25,
      sortable: false,
      renderCell: (params) => {
        if (params?.row?.comments) {
          return <CommentIcon sx={{ cursor: "pointer" }} onClick={() => setSelectedEmployeeForAudit(params?.row)} />;
        }
      },
    },
    {
      field: "removeOrAddEmployee",
      headerName: "",
      width: 20,
      renderCell: (params) => {
        if (!params?.row?.isRemoved) {
          return (
            <IconButton
              disabled={isDeletedBid(bidStatus) || isAwardedBid(bidStatus)}
              sx={{ cursor: "pointer" }}
              onClick={() => handleRemoveEmployee(params.row)}
            >
              <RemoveCircleOutline fontSize="small" />
            </IconButton>
          );
        }

        return (
          <Tooltip
            title={
              getEmployeeUnrankedCount(getEligibleEmployees(bidGroup)) > 0 && BID_GROUP_ADD_EMPLOYEE_WARNING_MESSAGE
            }
            placement="bottom"
            arrow
          >
            <div>
              <IconButton
                disabled={
                  isDeletedBid(bidStatus) ||
                  getEmployeeUnrankedCount(getEligibleEmployees(bidGroup)) > 0 ||
                  isAwardedBid(bidStatus)
                }
                sx={{ color: "secondary.swaGray", cursor: "pointer" }}
                onClick={() => handleAddRemovedEmployee(params.row)}
              >
                <AddCircle fontSize="small" />
              </IconButton>
            </div>
          </Tooltip>
        );
      },
    },
  ];

  columns.push(...baseColumns);
  columns.push(...contractBasedColumns);
  columns.push(...staticRightColumns);

  return columns;
};

export const isEmployeeEligibleForNCBidChoices = (employeeRow, initialBidGroup, bidStatus, userSessionEid) => {
  if (
    employeeRow.isRemoved ||
    employeeRow.eid === userSessionEid ||
    isPendingBid(bidStatus) ||
    isDeletedBid(bidStatus) ||
    isAwardedBid(bidStatus)
  )
    return false;
  else {
    let idx = initialBidGroup.findIndex((emp) => emp.eid === employeeRow.eid);
    if (idx === -1) return false;
  }
  return true;
};

const isEmployeeEligibleForBaseSchedule = (employeeRow, initialBidGroup, bidStatus, bidStation) => {
  if (
    employeeRow.isRemoved ||
    isDeletedBid(bidStatus) ||
    isAwardedBid(bidStatus) ||
    (employeeRow.isTemp && employeeRow.homeStation !== bidStation)
  )
    return false;
  else {
    let idx = initialBidGroup.findIndex((emp) => emp.eid === employeeRow.eid);
    if (idx === -1) return false;
  }
  return true;
};

export const handlePTOBalanceCellEditCommit = (params, e, bidGroup, setBidGroup, setError) => {
  const { id, value } = params;
  const selectedEmployeeForBalance = bidGroup?.find((item) => item.eid === id);
  if (isValidPTOBalance(value)) {
    const copiedBidGroup = copyJsonObject(bidGroup);
    const updatedBidGroup = copiedBidGroup.map((row) => (row.eid === id ? { ...row, ptoBalance: value } : { ...row }));
    setBidGroup(updatedBidGroup);
    setError(false);
  } else if (
    (value === "-" && selectedEmployeeForBalance?.ptoBalance === "-") ||
    (value === "" && (e.code === "Backspace" || e.code === "Delete")) ||
    value === undefined
  ) {
    setError(false);
  } else {
    setError(true);
  }
};

export const handleFTOBalanceCellEditCommit = (params, e, bidGroup, setBidGroup, setError) => {
  const { id, value } = params;
  const selectedEmployeeForBalance = bidGroup?.find((item) => item.eid === id);
  if (isValidFTOBalance(value)) {
    const copiedBidGroup = copyJsonObject(bidGroup);
    const updatedBidGroup = copiedBidGroup.map((row) => (row.eid === id ? { ...row, ftoBalance: value } : { ...row }));
    setBidGroup(updatedBidGroup);
    setError(false);
  } else if (
    (value === "-" && selectedEmployeeForBalance?.ftoBalance === "-") ||
    (value === "" && (e.code === "Backspace" || e.code === "Delete")) ||
    value === undefined
  ) {
    setError(false);
  } else {
    setError(true);
  }
};

export const handleGOCHOLBalanceCellEditCommit = (params, e, bidGroup, setBidGroup, setError) => {
  const { id, value } = params;
  const selectedEmployeeForBalance = bidGroup?.find((item) => item.eid === id);
  if (isValidGOCHOLBalance(value)) {
    const copiedBidGroup = copyJsonObject(bidGroup);
    const updatedBidGroup = copiedBidGroup.map((row) =>
      row.eid === id ? { ...row, gocHolBalance: value } : { ...row }
    );
    setBidGroup(updatedBidGroup);
    setError(false);
  } else if (
    (value === "-" && selectedEmployeeForBalance?.gocHolBalance === "-") ||
    (value === "" && (e.code === "Backspace" || e.code === "Delete")) ||
    value === undefined
  ) {
    setError(false);
  } else {
    setError(true);
  }
};

const isValidPTOBalance = (value) => {
  return /^(4[0-5]|3\d|2\d|1\d|\d)$/.test(value) && Number(value) >= 0 && Number(value) <= 45;
};

const isValidFTOBalance = (value) => {
  return /^(10|[0-9])$/.test(value) && Number(value) >= 0 && Number(value) <= 10;
};

const isValidGOCHOLBalance = (value) => {
  return /^([0-8])$/.test(value) && Number(value) >= 0 && Number(value) <= 8;
};

export const getEmployeesWithInvalidBalanceCount = (employees, bidType) => {
  if (bidType === BID_TYPE.NC_PTO_FTO) {
    return employees.filter(
      (employee) =>
        !employee.isRemoved && (!isValidPTOBalance(employee.ptoBalance) || !isValidFTOBalance(employee.ftoBalance))
    ).length;
  }

  return employees.filter(
    (employee) =>
      !employee.isRemoved && (!isValidPTOBalance(employee.ptoBalance) || !isValidGOCHOLBalance(employee.gocHolBalance))
  ).length;
};

export const parseNCPTOFTOBalancesCSV = (csvText) => {
  const rows = csvText
    .split("\n")
    .map((row) => row.trim())
    .filter((row) => row);

  if (rows.length < 2) {
    return { data: [], error: NC_PTO_FTO_BALANCES_IMPORT_ERROR_MESSAGE };
  }

  const headers = parseCSVRow(rows[0]).map((col) => col.toLowerCase());

  if (!headers.includes("eid") || !headers.includes("pto balance") || !headers.includes("fto balance")) {
    return { data: [], error: NC_PTO_FTO_BALANCES_IMPORT_ERROR_MESSAGE };
  }

  const data = rows.slice(1).map((row) => {
    const values = parseCSVRow(row);

    return headers.reduce((acc, key, i) => {
      acc[key] = values[i]?.trim() || "";
      return acc;
    }, {});
  });

  return { data, error: null };
};

export const validateNCPTOFTOBalancesCSV = (parsedData, bidGroup) => {
  const errors = [];

  parsedData.forEach((row, index) => {
    const rowNumber = index + 2;

    if (!/^e\d+$/.test(row.eid)) {
      errors.push(`Row ${rowNumber}: Invalid EID entered: ${row.eid}. Enter a valid EID including the "e" prefix.`);
    } else {
      const eidExistsInBidGroup = bidGroup.find((emp) => emp.eid === row.eid);
      if (!eidExistsInBidGroup) {
        errors.push(`Row ${rowNumber}: Invalid EID entered: ${row.eid}. Enter an EID from the current Bid Group.`);
      }
    }

    const ptoBalance = row["pto balance"];
    if (!isValidPTOBalance(ptoBalance)) {
      errors.push(`Row ${rowNumber}: Invalid PTO balance entered: ${ptoBalance}. Enter a number between 0 and 45.`);
    }

    const ftoBalance = row["fto balance"];
    if (!isValidFTOBalance(ftoBalance)) {
      errors.push(`Row ${rowNumber}: Invalid FTO balance entered: ${ftoBalance}. Enter a number between 0 and 10.`);
    }
  });

  return errors;
};

export const parseNCPTOGOCHOLBalancesCSV = (csvText) => {
  const rows = csvText
    .split("\n")
    .map((row) => row.trim())
    .filter((row) => row);

  if (rows.length < 2) {
    return { data: [], error: NC_PTO_GOC_HOL_BALANCES_IMPORT_ERROR_MESSAGE };
  }

  const headers = parseCSVRow(rows[0]).map((col) => col.toLowerCase());

  if (!headers.includes("eid") || !headers.includes("pto balance") || !headers.includes("goc-hol balance")) {
    return { data: [], error: NC_PTO_GOC_HOL_BALANCES_IMPORT_ERROR_MESSAGE };
  }

  const data = rows.slice(1).map((row) => {
    const values = parseCSVRow(row);

    return headers.reduce((acc, key, i) => {
      acc[key] = values[i]?.trim() || "";
      return acc;
    }, {});
  });

  return { data, error: null };
};

export const validateNCPTOGOCHOLBalancesCSV = (parsedData, bidGroup) => {
  const errors = [];

  parsedData.forEach((row, index) => {
    const rowNumber = index + 2;

    if (!/^e\d+$/.test(row.eid)) {
      errors.push(`Row ${rowNumber}: Invalid EID entered: ${row.eid}. Enter a valid EID including the "e" prefix.`);
    } else {
      const eidExistsInBidGroup = bidGroup.find((emp) => emp.eid === row.eid);
      if (!eidExistsInBidGroup) {
        errors.push(`Row ${rowNumber}: Invalid EID entered: ${row.eid}. Enter an EID from the current Bid Group.`);
      }
    }

    const ptoBalance = row["pto balance"];
    if (!isValidPTOBalance(ptoBalance)) {
      errors.push(`Row ${rowNumber}: Invalid PTO balance entered: ${ptoBalance}. Enter a number between 0 and 45.`);
    }

    const gocHolBalance = row["goc-hol balance"];
    if (!isValidGOCHOLBalance(gocHolBalance)) {
      errors.push(
        `Row ${rowNumber}: Invalid GOC-HOL balance entered: ${gocHolBalance}. Enter a number between 0 and 8.`
      );
    }
  });

  return errors;
};

export const getMinimumAllotmentValue = (schedMonth, schedYear, date, calcDailyAllotment) => {
  const month = convertMonthStringToInteger(schedMonth);
  const year = parseInt(schedYear);
  const thanksgivingDate = getThanksGivingDateOfTheYear(year);
  const luxonDate = convertIsoDateToLuxon(date);
  if ((month === 11 || month === 12) && isHoliday(luxonDate, year, thanksgivingDate)) {
    return 0;
  }
  return calcDailyAllotment.amOrPmCount;
};

export const generateNCBidChoiceRows = (choices, isReadOnly) => {
  if (isReadOnly) {
    return getNonEmptyChoices(choices).map((choice, index) => ({
      id: index + 1,
      date: choice.date,
      timeOffType: choice.timeOffType,
    }));
  }
  return choices.map((choice, index) => ({
    id: index + 1,
    date: choice.date,
    timeOffType: choice.timeOffType,
  }));
};

export const getEligibleEmployeesWithInvalidBalanceCount = (employees, bidType) => {
  if (bidType === BID_TYPE.NC_PTO_FTO) {
    return employees.filter(
      (employee) =>
        employee.isEligible &&
        !employee.isRemoved &&
        (!isValidPTOBalance(employee.ptoBalance) || !isValidFTOBalance(employee.ftoBalance))
    ).length;
  }

  return employees.filter(
    (employee) =>
      employee.isEligible &&
      !employee.isRemoved &&
      (!isValidPTOBalance(employee.ptoBalance) || !isValidGOCHOLBalance(employee.gocHolBalance))
  ).length;
};

// Below function create records for each day based on award results
export const processDayStateForNCAward = (dayState, bidGroup) => {
  return dayState.flatMap((obj) => {
    const result = [];
    const date = obj.date;
    const awardedEmps = obj.awardedEmps || [];
    const amOrPmCount = obj.amOrPmCount || 0;

    // Filter awarded employees who have awardedDays and exist in bidGroup
    const eligibleEmps = awardedEmps.filter((awardedEmp) => {
      return (
        Array.isArray(awardedEmp.awardedDays) &&
        awardedEmp.awardedDays.length > 0 &&
        bidGroup.some((bidGroupEmp) => bidGroupEmp.eid === awardedEmp.eid)
      );
    });

    // Create records based on amOrPmCount
    for (let i = 0; i < amOrPmCount; i++) {
      if (eligibleEmps[i]) {
        result.push({ ...eligibleEmps[i], date: date });
      } else {
        result.push({ date: date });
      }
    }

    return result;
  });
};

export const generateNCDayStateRows = (allotmentDayState, bidGroup) => {
  const ncResults = processDayStateForNCAward(allotmentDayState, bidGroup);
  return ncResults.sort((a, b) => a.date.localeCompare(b.date)).map((obj, index) => ({ id: index + 1, ...obj }));
};

export const getUnAwardedTableColumns = ({ station, bidReferenceNumber }) => {
  let columns = [
    {
      field: "awardType",
      headerName: "Bid",
      sortable: false,
      flex: 0.15,
    },
    {
      field: "rank",
      headerName: "Rank",
      sortable: false,
      flex: 0.15,
    },
    {
      field: "eid",
      headerName: "EID",
      sortable: false,
      flex: 0.2,
    },
    {
      field: "name",
      headerName: "Name",
      sortable: false,
      flex: 0.5,
      renderCell: (params) => {
        return (
          <>
            {params.row.lastName && params.row.firstName ? params.row.lastName + ", " + params.row.firstName : ""}
            {params.row.isTemp && params.row.homeStation !== station && (
              <Chip sx={{ marginLeft: "5px" }} label={EMPLOYEE_TEMP_LABEL} />
            )}
          </>
        );
      },
    },
    {
      field: "baseSchedule",
      headerName: "Base Schedule",
      type: "string",
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        if (params.row.awardType === "NS") {
          return (
            <Link
              to={{
                pathname: `/${FEATURES.SCHEDULE_MANAGEMENT}/${bidReferenceNumber}/${params.row.eid}`,
              }}
              target="_blank"
            >
              <IconButton>
                <CalendarMonthIcon />
              </IconButton>
            </Link>
          );
        }
      },
      flex: 0.15,
    },
  ];

  return columns;
};

export const getNCColumns = ({ station, handleAwardedNameClick }) => {
  let columns = [
    {
      field: "date",
      headerName: "Day",
      sortable: false,
      flex: 0.15,
      renderCell: (params) => {
        return params.row.date ? DateTime.fromFormat(params.row.date, "yyyy-MM-dd").toFormat("MM/dd") : params.row.id;
      },
    },
    {
      field: "timeOffType",
      headerName: "Type",
      sortable: false,
      flex: 0.15,
      renderCell: (params) => {
        return params.row.awardedDays?.find((awardedDay) => awardedDay.date === params.row.date).timeOffType;
      },
    },
    {
      field: "awardType",
      headerName: "Award",
      sortable: false,
      flex: 0.15,
      renderCell: (params) => {
        return params.row.awardType;
      },
    },
    {
      field: "awardedRank",
      headerName: "Rank",
      sortable: false,
      flex: 0.15,
      renderCell: (params) => {
        return params.row.awardedRank;
      },
    },
    {
      field: "awardedEid",
      headerName: "EID",
      sortable: false,
      flex: 0.15,
      renderCell: (params) => {
        return params.row.eid;
      },
    },
    {
      field: "awardedName",
      headerName: "Name",
      sortable: false,
      flex: 0.4,
      renderCell: (params) => {
        return (
          <>
            <Typography
              style={{ cursor: "pointer" }}
              fontSize="14px"
              onClick={() => {
                handleAwardedNameClick(params.row);
              }}
            >
              <u>
                {params.row.lastName && params.row.firstName ? params.row.lastName + ", " + params.row.firstName : ""}
              </u>
            </Typography>
            {params.row.isTemp && params.row.homeStation !== station && (
              <Chip sx={{ marginLeft: "5px" }} label={EMPLOYEE_TEMP_LABEL} />
            )}
          </>
        );
      },
    },
  ];
  return columns;
};

export const hasEmployeesMarkedNS = (employees) => {
  return employees.filter((emp) => emp?.awardType === NC_BID_AWARD_TYPE.NO_SCHEDULE).length > 0;
};
